import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Panel, TokenLogo } from "../components/common/Panel";
import { useAdmin, fileToDataUrl } from "../hooks/useAdmin";
import { verifyToken, unverify, getFee, setFee, getTagRequests, approveTag, rejectTag, setTag, removeTag, getTags } from "../lib/api";
import { shorten } from "../lib/format";

export default function Admin() {
  const { admin, loading, doLogin, doLogout } = useAdmin();

  // login state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginErr, setLoginErr] = useState("");
  const [busy, setBusy] = useState(false);

  // token verify state
  const [taddr, setTaddr] = useState("");
  const [logo, setLogo] = useState("");
  const [logoName, setLogoName] = useState("");
  const [vbusy, setVbusy] = useState(false);
  const [vmsg, setVmsg] = useState(null);
  const [verr, setVerr] = useState("");

  // unverify state
  const [unaddr, setUnaddr] = useState("");
  const [unmsg, setUnmsg] = useState("");

  // network fee state
  const [fee, setFeeState] = useState(null);
  const [feeInput, setFeeInput] = useState("");
  const [feeBusy, setFeeBusy] = useState(false);
  const [feeMsg, setFeeMsg] = useState("");
  // public name tags
  const [tagReqs, setTagReqs] = useState([]);
  const [tags, setTags] = useState([]);
  const [tagAddr, setTagAddr] = useState("");
  const [tagLbl, setTagLbl] = useState("");
  const [tagSaveMsg, setTagSaveMsg] = useState("");

  const loadTags = () => {
    getTagRequests("pending").then((d) => setTagReqs(d.requests || [])).catch(() => {});
    getTags().then((d) => setTags(d.tags || [])).catch(() => {});
  };

  useEffect(() => {
    if (!admin) return;
    getFee().then((f) => { setFeeState(f); setFeeInput(String(f.minGasPriceGwei)); }).catch(() => {});
    loadTags();
    const id = setInterval(loadTags, 6000);
    return () => clearInterval(id);
  }, [admin]); // eslint-disable-line

  const doApprove = async (id) => { await approveTag(id).catch(() => {}); loadTags(); };
  const doReject = async (id) => { await rejectTag(id).catch(() => {}); loadTags(); };
  const doRemoveTag = async (address) => { await removeTag(address).catch(() => {}); loadTags(); };
  const doSetTag = async () => {
    setTagSaveMsg("");
    try {
      const r = await setTag(tagAddr.trim(), tagLbl.trim());
      setTagSaveMsg(`Tag set: ${r.label} for ${shorten(r.address, 8, 6)}`);
      setTagAddr(""); setTagLbl(""); loadTags();
    } catch (err) { setTagSaveMsg(err?.response?.data?.detail || "Failed"); }
  };
  const validTag = /^0x[a-fA-F0-9]{40}$/.test(tagAddr.trim()) && tagLbl.trim().length > 0;

  if (loading) {
    return <div className="text-center py-24" style={{ color: "var(--muted)" }} data-testid="admin-loading">Loading…</div>;
  }

  if (!admin) {
    const submit = async (e) => {
      e.preventDefault();
      setLoginErr(""); setBusy(true);
      try {
        await doLogin(email.trim(), password);
      } catch (err) {
        setLoginErr(err?.response?.data?.detail || "Invalid email or password");
      } finally {
        setBusy(false);
      }
    };
    return (
      <div className="fade-in max-w-md mx-auto mt-10" data-testid="admin-login-page">
        <div className="mb-5 text-center">
          <span className="eyebrow" style={{ color: "var(--amber)" }}>Restricted</span>
          <h1 className="font-display text-[26px] font-extrabold mt-1">Admin Console</h1>
          <p className="text-[13px] mt-1" style={{ color: "var(--muted)" }}>Sign in to verify tokens and manage the registry.</p>
        </div>
        <Panel title="Sign In">
          <form onSubmit={submit} className="p-6">
            <label className="eyebrow block mb-2">Email</label>
            <input className="input-dark mb-4" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@blokista.io" data-testid="admin-email-input" autoComplete="username" />
            <label className="eyebrow block mb-2">Password</label>
            <input className="input-dark mb-4" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••" data-testid="admin-password-input" autoComplete="current-password" />
            <button type="submit" className="btn-amber w-full" disabled={busy || !email || !password} data-testid="admin-login-btn">
              {busy ? <><i className="fa-solid fa-circle-notch fa-spin mr-2" /> Signing in…</> : <><i className="fa-solid fa-lock mr-2" /> Sign In</>}
            </button>
            {loginErr && <div className="mt-4 text-[13px] p-3 rounded-lg" style={{ background: "rgba(242,85,90,0.1)", color: "var(--red)" }} data-testid="admin-login-error">{loginErr}</div>}
          </form>
        </Panel>
      </div>
    );
  }

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setLogoName(f.name);
    setLogo(await fileToDataUrl(f));
  };

  const verify = async (e) => {
    e.preventDefault();
    setVerr(""); setVmsg(null); setVbusy(true);
    try {
      const r = await verifyToken({ address: taddr.trim(), logo });
      setVmsg(r);
      setTaddr(""); setLogo(""); setLogoName("");
    } catch (err) {
      setVerr(err?.response?.data?.detail || "Verification failed");
    } finally {
      setVbusy(false);
    }
  };

  const doUnverify = async () => {
    setUnmsg("");
    try {
      await unverify(unaddr.trim());
      setUnmsg(`Verification removed for ${shorten(unaddr.trim(), 8, 6)}`);
      setUnaddr("");
    } catch (err) {
      setUnmsg(err?.response?.data?.detail || "Failed");
    }
  };

  const saveFee = async () => {
    setFeeBusy(true); setFeeMsg("");
    try {
      const g = parseFloat(feeInput);
      const r = await setFee(g);
      setFeeMsg(`Network fee set to ${r.minGasPriceGwei} gwei (${r.nodesUpdated}/${r.total} nodes updated)`);
      getFee().then(setFeeState).catch(() => {});
    } catch (err) {
      setFeeMsg(err?.response?.data?.detail || "Failed to set fee");
    } finally { setFeeBusy(false); }
  };

  const validT = /^0x[a-fA-F0-9]{40}$/.test(taddr.trim());
  const validU = /^0x[a-fA-F0-9]{40}$/.test(unaddr.trim());

  return (
    <div className="fade-in" data-testid="admin-panel-page">
      <div className="mb-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <span className="eyebrow" style={{ color: "var(--amber)" }}>Admin Console</span>
          <h1 className="font-display text-[24px] font-extrabold mt-1">Token Registry Management</h1>
          <p className="text-[13px] mt-1" style={{ color: "var(--muted)" }}>Signed in as <span className="mono" style={{ color: "var(--text)" }}>{admin.email}</span></p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/governance" className="nav-link" data-testid="admin-goto-governance"><i className="fa-solid fa-shield-halved mr-1" /> Governance</Link>
          <button className="nav-link" onClick={doLogout} data-testid="admin-logout-btn"><i className="fa-solid fa-arrow-right-from-bracket mr-1" /> Sign Out</button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Panel title="Verify Token" subtitle="BCC-20" testId="panel-verify-token">
          <form onSubmit={verify} className="p-5">
            <label className="eyebrow block mb-2">Token Contract Address</label>
            <input className="input-dark mb-4" value={taddr} onChange={(e) => setTaddr(e.target.value)} placeholder="0x…" data-testid="verify-token-address" />

            <label className="eyebrow block mb-2">Token Logo</label>
            <div className="flex items-center gap-3 mb-4">
              <TokenLogo logo={logo} symbol="?" size={44} />
              <label className="btn-amber py-2 px-4 text-[12px] cursor-pointer" data-testid="verify-token-logo-label">
                <i className="fa-solid fa-upload mr-1.5" /> {logoName ? "Change" : "Upload logo"}
                <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="verify-token-logo-input" />
              </label>
              {logoName && <span className="text-[12px] mono truncate" style={{ color: "var(--muted)" }}>{logoName}</span>}
            </div>

            <button type="submit" className="btn-amber w-full" disabled={vbusy || !validT || !logo} data-testid="verify-token-submit">
              {vbusy ? <><i className="fa-solid fa-circle-notch fa-spin mr-2" /> Verifying…</> : <><i className="fa-solid fa-circle-check mr-2" /> Verify Token</>}
            </button>
            {!logo && validT && <div className="mt-2 text-[11.5px]" style={{ color: "var(--muted-2)" }}>Upload a logo image to enable verification.</div>}
            {verr && <div className="mt-3 text-[12px] p-2 rounded" style={{ background: "rgba(242,85,90,0.1)", color: "var(--red)" }} data-testid="verify-token-error">{verr}</div>}
            {vmsg && (
              <div className="mt-3 p-3 rounded-lg" style={{ background: "rgba(52,211,153,0.08)", border: "1px solid rgba(52,211,153,0.25)" }} data-testid="verify-token-success">
                <div style={{ color: "var(--green)" }}><i className="fa-solid fa-circle-check mr-1" /> Token verified!</div>
                <div className="mt-1 text-[12px]" style={{ color: "var(--muted)" }}>
                  <Link to={`/token/${vmsg.address}`} className="link-amber mono">{shorten(vmsg.address, 10, 8)}</Link> now shows a verified badge and your logo.
                </div>
              </div>
            )}
          </form>
        </Panel>

        <Panel title="Remove Verification" testId="panel-unverify">
          <div className="p-5">
            <p className="text-[13px] mb-4" style={{ color: "var(--muted)" }}>Revoke the verified status of a token or contract address.</p>
            <label className="eyebrow block mb-2">Address</label>
            <input className="input-dark mb-4" value={unaddr} onChange={(e) => setUnaddr(e.target.value)} placeholder="0x…" data-testid="unverify-address" />
            <button className="nav-link w-full" style={{ borderColor: "var(--red)", color: "var(--red)" }} onClick={doUnverify} disabled={!validU} data-testid="unverify-submit">
              <i className="fa-solid fa-ban mr-1.5" /> Remove Verification
            </button>
            {unmsg && <div className="mt-3 text-[12px]" style={{ color: "var(--muted)" }} data-testid="unverify-result">{unmsg}</div>}
          </div>
        </Panel>
      </div>

      <Panel title="Network Fee" subtitle="protocol gas price" className="mt-5" testId="panel-network-fee">
        <div className="p-5">
          <p className="text-[13px] mb-4" style={{ color: "var(--muted)" }}>
            Set the minimum gas price for the whole network. Every transaction pays <span className="mono">gasUsed × fee</span> in {fee?.symbol || "BCC"} to validators. Applied live to all 4 nodes.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="panel p-4"><div className="eyebrow mb-1">Current Fee</div><div className="stat-value text-[20px]" style={{ color: "var(--amber)" }} data-testid="fee-current">{fee ? `${fee.minGasPriceGwei} gwei` : "—"}</div></div>
            <div className="panel p-4"><div className="eyebrow mb-1">Effective Gas Price</div><div className="stat-value text-[20px]">{fee ? `${fee.gasPriceGwei} gwei` : "—"}</div></div>
            <div className="panel p-4"><div className="eyebrow mb-1">Sample Transfer Fee</div><div className="stat-value text-[20px]">{fee ? `${fee.sampleTransferFee} ${fee.symbol}` : "—"}</div></div>
          </div>
          <label className="eyebrow block mb-2">New Fee (gwei)</label>
          <div className="flex gap-2">
            <input className="input-dark text-[13px]" type="number" min="0" step="0.1" value={feeInput} onChange={(e) => setFeeInput(e.target.value)} placeholder="e.g. 1" data-testid="fee-input" />
            <button className="btn-amber px-5 whitespace-nowrap" onClick={saveFee} disabled={feeBusy || feeInput === ""} data-testid="fee-save-btn">{feeBusy ? "Saving…" : "Set Fee"}</button>
          </div>
          {feeMsg && <div className="mt-3 text-[12px] p-2 rounded" style={{ background: "rgba(52,211,153,0.08)", color: "var(--green)" }} data-testid="fee-result">{feeMsg}</div>}
        </div>
      </Panel>

      {/* Public Name Tags management */}
      <div className="mt-5">
        <h2 className="font-display text-[16px] font-bold mb-3">Public Name Tags</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Panel title="Pending Requests" action={<span className="pill pill-amber">{tagReqs.length}</span>} testId="panel-tag-requests">
            <div className="p-2">
              {tagReqs.map((r, i) => (
                <div key={r.id} className="px-3 py-3 rounded-lg mb-2" style={{ background: "var(--bg-2)", border: "1px solid var(--border)" }} data-testid={`tag-req-${i}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div>
                      <span className="pill pill-amber mb-1"><i className="fa-solid fa-tag mr-1" />{r.label}</span>
                      <div className="mono text-[11.5px]" style={{ color: "var(--muted)" }}>{shorten(r.address, 10, 8)}</div>
                      {(r.contact || r.note) && <div className="text-[11px] mt-1" style={{ color: "var(--muted-2)" }}>{r.contact}{r.contact && r.note ? " · " : ""}{r.note}</div>}
                    </div>
                    <div className="flex gap-2">
                      <button className="btn-amber py-1.5 px-3 text-[11px]" onClick={() => doApprove(r.id)} data-testid={`tag-approve-${i}`}><i className="fa-solid fa-check mr-1" />Approve</button>
                      <button className="nav-link py-1.5 px-3 text-[11px]" style={{ borderColor: "var(--red)", color: "var(--red)" }} onClick={() => doReject(r.id)} data-testid={`tag-reject-${i}`}><i className="fa-solid fa-xmark mr-1" />Reject</button>
                    </div>
                  </div>
                </div>
              ))}
              {tagReqs.length === 0 && <div className="text-center py-6 text-[13px]" style={{ color: "var(--muted)" }}>No pending tag requests.</div>}
            </div>
          </Panel>

          <div className="flex flex-col gap-5">
            <Panel title="Approved Tags" action={<span className="pill pill-green">{tags.length}</span>} testId="panel-approved-tags">
              <div className="p-2">
                {tags.map((t, i) => (
                  <div key={t.address} className="flex items-center justify-between px-3 py-2.5" style={{ borderBottom: "1px solid rgba(30,40,49,0.5)" }} data-testid={`approved-tag-${i}`}>
                    <div>
                      <span className="pill pill-amber mb-1">{t.label}</span>
                      <div className="mono text-[11.5px]" style={{ color: "var(--muted)" }}><a href={`/address/${t.address}`} className="link-amber">{shorten(t.address, 8, 6)}</a></div>
                    </div>
                    <button className="link-amber text-[11.5px]" onClick={() => doRemoveTag(t.address)} data-testid={`tag-remove-${i}`}>remove</button>
                  </div>
                ))}
                {tags.length === 0 && <div className="text-center py-6 text-[13px]" style={{ color: "var(--muted)" }}>No tags yet.</div>}
              </div>
            </Panel>

            <Panel title="Set Tag Directly" testId="panel-set-tag">
              <div className="p-4">
                <label className="eyebrow block mb-1.5">Address</label>
                <input className="input-dark text-[12px] mb-2" value={tagAddr} onChange={(e) => setTagAddr(e.target.value)} placeholder="0x…" data-testid="set-tag-address" />
                <label className="eyebrow block mb-1.5">Label</label>
                <input className="input-dark text-[12px] mb-3" value={tagLbl} onChange={(e) => setTagLbl(e.target.value)} placeholder='e.g. "Blokista: Faucet"' data-testid="set-tag-label" />
                <button className="btn-amber w-full" onClick={doSetTag} disabled={!validTag} data-testid="set-tag-btn"><i className="fa-solid fa-tag mr-1.5" />Set Public Tag</button>
                {tagSaveMsg && <div className="mt-2 text-[12px]" style={{ color: "var(--green)" }} data-testid="set-tag-result">{tagSaveMsg}</div>}
              </div>
            </Panel>
          </div>
        </div>
      </div>
    </div>
  );
}
